import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { ApiClientError } from '@olmt/shared'
import { api, describeError, type SessionUser } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { AccountGateNotice } from '../components/AccountGateNotice'
import { Spinner } from '../components/Spinner'

/**
 * The console's front door: email + password (docs/CONTRACT.md §4). An account that is not an
 * approved administrator never gets a session stored — the gate's answer is shown right here, so
 * "you cannot work here yet" is the last thing the sign-in form says, not a page the user has to
 * infer from a failed request.
 */
export function SignInPage() {
  const navigate = useNavigate()
  const token = useAuthStore((state) => state.token)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refused, setRefused] = useState<SessionUser | null>(null)

  if (token) return <Navigate to="/" replace />

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const trimmedEmail = email.trim()
    if (!trimmedEmail || !password) {
      setError('Enter your email and password.')
      return
    }
    setBusy(true)
    setError(null)
    setRefused(null)
    try {
      const response = await api.login({
        email: trimmedEmail,
        password,
        deviceName: 'Admin console',
        platform: 'web',
      })
      if (response.user.status === 'approved' && response.user.isAdmin) {
        useAuthStore.getState().setSession(response.token, response.user)
        void navigate('/queue', { replace: true })
      } else {
        setRefused(response.user)
      }
    } catch (caught) {
      setError(
        caught instanceof ApiClientError && caught.code === 'not_approved'
          ? describeError(caught, 'This account is not approved yet.')
          : describeError(caught, 'Could not sign in. Try again.'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center overflow-y-auto px-6">
      <main className="w-full max-w-sm py-10">
        {/* The front door. One warm point of light, the name of the house, one sentence about what
            this place is, then the two fields that get you in. */}
        <span
          aria-hidden="true"
          className="mb-5 flex size-12 items-center justify-center rounded-full bg-accent/12 ring-1 ring-accent/30 ring-inset"
        >
          <span className="size-3 rounded-full bg-accent shadow-[0_0_16px_3px_rgb(232_176_75/0.45)]" />
        </span>
        <h1 className="text-display font-semibold tracking-tight text-ink">
          olmt<span className="text-accent">·</span>call
          <span className="ms-2 align-middle text-ui font-medium text-muted">console</span>
        </h1>
        <p className="mt-2 text-ui text-muted">Sign in with an administrator account.</p>

        {refused ? (
          <div className="mt-8">
            <AccountGateNotice
              user={refused}
              actions={[
                {
                  label: 'Back to sign in',
                  onClick: () => {
                    setRefused(null)
                    setPassword('')
                  },
                  primary: true,
                },
              ]}
            />
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-meta text-muted">
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                autoFocus
                spellCheck={false}
                className="min-h-12 rounded-panel bg-surface2 px-3.5 text-body text-ink ring-1 ring-line ring-inset placeholder:text-muted"
              />
            </label>

            <label className="flex flex-col gap-1.5 text-meta text-muted">
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                className="min-h-12 rounded-panel bg-surface2 px-3.5 text-body text-ink ring-1 ring-line ring-inset placeholder:text-muted"
              />
            </label>

            {error && (
              <p role="alert" className="text-ui text-danger">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="press mt-1 flex min-h-12 items-center justify-center gap-2 rounded-full bg-accent text-body font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
            >
              {busy && <Spinner size={18} label="Signing in" />}
              Sign in
            </button>

            <p className="text-meta text-muted">
              No account yet? Sign up in the chat app — an administrator approves new accounts from
              this console.
            </p>
          </form>
        )}
      </main>
    </div>
  )
}
